import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface NoShowRatePeriodParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface NoShowRateOverviewMetric {
  id: string
  leftLabel: string
  value: string
}

export interface NoShowOverviewLinePoint {
  label: string
  value: number
}

export interface NoShowOverviewLineChart {
  data: NoShowOverviewLinePoint[]
  yDomain: [number, number]
  yTicks: number[]
}

export interface NoShowRateOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  lostChairTime: string
  scheduledPct: number
  scheduledPctDisplay: string
  noShowCount: number
  metrics: NoShowRateOverviewMetric[]
  lineChart: NoShowOverviewLineChart
}

export interface NoShowRateDetailKpis {
  noShowRate: string
  noShowRateGoal: string | null
  noShows: number
  cancellations: number
  lostChairTime: string
  lostProduction: string
  rescheduleRate: string
  comparisonLabel: string
  rateDelta: string
  rateTrend: 'up' | 'down'
  noShowsDelta: string
  noShowsTrend: 'up' | 'down'
  lostProductionDelta: string
  lostProductionTrend: 'up' | 'down'
  rescheduleRateDelta: string
  rescheduleRateTrend: 'up' | 'down'
}

export interface NoShowRateChartPoint {
  label: string
  noShows: number
  cancellations: number
}

export interface NoShowRateChartResult {
  data: NoShowRateChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface NoShowRateByProviderRow {
  id: string
  provider: string
  scheduled: number
  noShows: number
  cancellations: number
  noShowRate: string
  lostProduction: string
  rescheduled: number
}

export interface NoShowRateByPeriodRow {
  id: string
  timePeriod: string
  scheduled: number
  noShows: number
  cancellations: number
  noShowRate: string
  lostProduction: string
  rescheduled: number
}

export interface NoShowRateDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: NoShowRateDetailKpis
  chart: NoShowRateChartResult
  byProvider: NoShowRateByProviderRow[]
  byPeriod: NoShowRateByPeriodRow[]
}

export const getNoShowRateOverview = async (
  params: NoShowRatePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<NoShowRateOverviewResult> => {
  const response = await axios.get<ApiResponse<NoShowRateOverviewResult>>(
    `${BUSINESS_LAYER_API}/api/v1/dashboard/no-show-rate/overview`,
    { params, signal: options?.signal },
  )
  return response.data.data
}

export const getNoShowRateDetail = async (
  params: NoShowRatePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<NoShowRateDetailResult> => {
  const response = await axios.get<ApiResponse<NoShowRateDetailResult>>(
    `${BUSINESS_LAYER_API}/api/v1/dashboard/no-show-rate/detail`,
    { params, signal: options?.signal },
  )
  return response.data.data
}
